import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import {
  runMigrationFunc,
  runSeederFunc,
  runSeederByClassFunc,
  clearMigrationOutput,
  clearSeederOutput,
  clearSeederByClassOutput,
} from "../../../store/custom-actions/migration-seeder-actions/migrationSeederSlicer";
import { Card, Form, Button, Alert } from "react-bootstrap";

const PHP_ACCEPT = ".php";
const MAX_FILE_SIZE = 512 * 1024; // 512 KB

function OutputBlock({ output, error, onClear, label }) {
  if (!output && !error) return null;
  return (
    <div className="mt-3">
      {output && (
        <pre className="bg-white border rounded p-3 small mb-2" style={{ maxHeight: 200, overflow: "auto" }}>
          {output}
        </pre>
      )}
      {error && (
        <Alert variant="danger" className="small mb-2">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </Alert>
      )}
      {onClear && (
        <Button size="sm" variant="outline-secondary" onClick={onClear}>
          {label}
        </Button>
      )}
    </div>
  );
}

export default function MigrationSeederPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    migration: { loading: migrationLoading, output: migrationOutput, error: migrationError },
    seeder: { loading: seederLoading, output: seederOutput, error: seederError },
    seederByClass: { loading: seederByClassLoading, output: seederByClassOutput, error: seederByClassError },
  } = useSelector((state) => state.migrationSeederSlicer);

  const [migrationFile, setMigrationFile] = useState(null);
  const [seederFile, setSeederFile] = useState(null);
  const [seederClassInput, setSeederClassInput] = useState("");
  const [seederClassOnly, setSeederClassOnly] = useState("");
  const [fileError, setFileError] = useState({ migration: "", seeder: "" });

  const validatePhpFile = (file, maxKb = 512) => {
    if (!file) return null;
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".php")) return t("File must be a PHP file (.php)");
    if (file.size > maxKb * 1024) return t("File size must be less than {{max}} KB", { max: maxKb });
    return null;
  };

  const handleRunMigration = (e) => {
    e.preventDefault();
    setFileError((prev) => ({ ...prev, migration: "" }));
    const err = validatePhpFile(migrationFile);
    if (err) {
      setFileError((prev) => ({ ...prev, migration: err }));
      return;
    }
    if (!migrationFile) return;
    const formData = new FormData();
    formData.append("migration", migrationFile);
    dispatch(runMigrationFunc(formData));
  };

  const handleRunSeeder = (e) => {
    e.preventDefault();
    setFileError((prev) => ({ ...prev, seeder: "" }));
    const err = validatePhpFile(seederFile);
    if (err) {
      setFileError((prev) => ({ ...prev, seeder: err }));
      return;
    }
    if (!seederFile) return;
    const formData = new FormData();
    formData.append("seeder", seederFile);
    if (seederClassInput.trim()) formData.append("seeder_class", seederClassInput.trim());
    dispatch(runSeederFunc(formData));
  };

  const handleRunSeederByClass = (e) => {
    e.preventDefault();
    const className = seederClassOnly.trim();
    if (!className) return;
    dispatch(runSeederByClassFunc(className));
  };

  return (
    <>
      <PageTitle motherMenu={t("Dashboard")} activeMenu={t("Migration & Seeder")} />

      <div className="row">
        {/* Run Migration */}
        <div className="col-12">
          <Card className="card-full">
            <Card.Header>
              <Card.Title className="card-title">{t("Run Migration")}</Card.Title>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                {t("Upload a PHP migration file. It will be executed then removed. Max 512 KB.")}
              </p>
              <Form onSubmit={handleRunMigration}>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="file"
                    accept={PHP_ACCEPT}
                    onChange={(e) => setMigrationFile(e.target.files?.[0] || null)}
                  />
                  {fileError.migration && (
                    <Form.Text className="text-danger">{fileError.migration}</Form.Text>
                  )}
                </Form.Group>
                <Button type="submit" variant="primary" disabled={migrationLoading || !migrationFile}>
                  {migrationLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {t("Running...")}
                    </>
                  ) : (
                    t("Run Migration")
                  )}
                </Button>
              </Form>
              <OutputBlock
                output={migrationOutput}
                error={migrationError}
                onClear={() => dispatch(clearMigrationOutput())}
                label={t("Clear")}
              />
            </Card.Body>
          </Card>
        </div>

        {/* Run Seeder (upload) */}
        <div className="col-12 mt-4">
          <Card className="card-full">
            <Card.Header>
              <Card.Title className="card-title">{t("Run Seeder (Upload File)")}</Card.Title>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                {t("Upload a PHP seeder file. Optionally specify the seeder class name (e.g. Database\\Seeders\\MySeeder).")}
              </p>
              <Form onSubmit={handleRunSeeder}>
                <Form.Group className="mb-2">
                  <Form.Label>{t("Seeder file")}</Form.Label>
                  <Form.Control
                    type="file"
                    accept={PHP_ACCEPT}
                    onChange={(e) => setSeederFile(e.target.files?.[0] || null)}
                  />
                  {fileError.seeder && (
                    <Form.Text className="text-danger">{fileError.seeder}</Form.Text>
                  )}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="optional">{t("Seeder class (optional)")}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Database\Seeders\MySeeder"
                    value={seederClassInput}
                    onChange={(e) => setSeederClassInput(e.target.value)}
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={seederLoading || !seederFile}>
                  {seederLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {t("Running...")}
                    </>
                  ) : (
                    t("Run Seeder")
                  )}
                </Button>
              </Form>
              <OutputBlock
                output={seederOutput}
                error={seederError}
                onClear={() => dispatch(clearSeederOutput())}
                label={t("Clear")}
              />
            </Card.Body>
          </Card>
        </div>

        {/* Run Seeder by class name */}
        <div className="col-12 mt-4">
          <Card className="card-full">
            <Card.Header>
              <Card.Title className="card-title">{t("Run Seeder by Class Name")}</Card.Title>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                {t("Run a seeder that already exists in the project by its full class name (e.g. Database\\Seeders\\PermissionSeeder).")}
              </p>
              <Form onSubmit={handleRunSeederByClass}>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Database\Seeders\PermissionSeeder"
                    value={seederClassOnly}
                    onChange={(e) => setSeederClassOnly(e.target.value)}
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={seederByClassLoading || !seederClassOnly.trim()}>
                  {seederByClassLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {t("Running...")}
                    </>
                  ) : (
                    t("Run Seeder")
                  )}
                </Button>
              </Form>
              <OutputBlock
                output={seederByClassOutput}
                error={seederByClassError}
                onClear={() => dispatch(clearSeederByClassOutput())}
                label={t("Clear")}
              />
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
}
